import { io, type Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '@shared/protocol';
import { useAuthStore } from '@/auth/authStore';
import { SERVER_URL } from '@/config/serverUrl';

// Socket.IO needs an absolute URL. SERVER_URL is empty when running same-origin
// on the web (phone hitting our nginx) — fall back to the current page origin
// so the socket lands on the SAME host that served us (nginx upgrades to ws).
// `localhost:8787` was a dev convenience that broke on phones (localhost = phone).
const SERVER_URL_OR_LOCALHOST =
  SERVER_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8787');

type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: AppSocket | null = null;

interface ConnectOpts {
  url?: string;
  token?: string;
}

/**
 * Lazily connect to the match server. Reuses an existing connection so the
 * socket survives a screen change. The JWT is read from the auth store at
 * connect time so a freshly-issued token after login takes effect.
 */
export function getSocket(opts: ConnectOpts = {}): AppSocket {
  const token = opts.token ?? useAuthStore.getState().token ?? null;
  if (socket && socket.connected) return socket;
  // If a stale socket exists with a different token, recycle it.
  if (socket) {
    const oldToken = (socket.auth as { token?: string } | undefined)?.token;
    if (oldToken !== token) {
      try {
        socket.disconnect();
      } catch {
        /* ignore */
      }
      socket = null;
    }
  }
  if (socket) return socket;

  const url = opts.url ?? SERVER_URL_OR_LOCALHOST;
  socket = io(url, {
    transports: ['websocket', 'polling'],
    auth: { token },
    // ngrok free tier shows an interstitial for the long-polling handshake
    // unless this header is present. WebSocket upgrade is unaffected but the
    // initial polling fallback needs it.
    extraHeaders: { 'ngrok-skip-browser-warning': 'true' },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
  }) as AppSocket;
  return socket;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}

export function isConnected(): boolean {
  return socket?.connected ?? false;
}
